import { useEffect, useRef, useState } from "react";
import {
  Modal,
  Form,
  Select,
  Input,
  Switch,
  Button,
  Row,
  Col,
  Divider,
  InputNumber,
  Typography,
  Space,
  Tooltip,
  Alert,
  Tag,
  message,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  ExperimentOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  QuestionCircleOutlined,
  WarningFilled,
} from "@ant-design/icons";
import {
  get_supported_ai_provider_keys,
  list_ai_providers,
  test_ai_provider_connection,
} from "../../api/admin.apis";
import SmokeTestResult from "./SmokeTestResult";

const { Option } = Select;
const { Text } = Typography;

// Cheapest small model per provider — used as a sensible default for the
// "test connection" panel. The admin can override before running the test.
const DEFAULT_TEST_MODEL = {
  openai: "gpt-4o-mini",
  anthropic: "claude-haiku-4-5",
  gemini: "gemini-2.0-flash-lite",
  groq: "llama-3.1-8b-instant",
  openrouter: "deepseek/deepseek-r1-0528:free",
};

const DEFAULT_TEST_PROMPT = "Reply with the word OK.";

const extractApiError = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (data.errors && typeof data.errors === "object") {
    const firstKey = Object.keys(data.errors)[0];
    if (firstKey) return data.errors[firstKey];
  }
  return data.message || fallback;
};

const CreateProviderModal = ({ visible, loading, onSubmit, onClose }) => {
  const [form] = Form.useForm();
  const [supportedKeys, setSupportedKeys] = useState([]);
  const [usedKeys, setUsedKeys] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  // Test panel state (standalone "test connection" widget)
  const [testModelId, setTestModelId] = useState("");
  const [testPrompt, setTestPrompt] = useState(DEFAULT_TEST_PROMPT);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Per-model test state for the Form.List rows.
  // Keyed by `model_id` so it stays consistent across re-orders/removes.
  const [modelTestResults, setModelTestResults] = useState({});
  const [modelTestLoading, setModelTestLoading] = useState({});

  // Pre-submit gate state
  const [preflightRunning, setPreflightRunning] = useState(false);
  const [creationBlocked, setCreationBlocked] = useState(false);
  const [failedModels, setFailedModels] = useState([]);
  const pendingPayloadRef = useRef(null);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setOptionsLoading(true);

    Promise.all([get_supported_ai_provider_keys(), list_ai_providers()])
      .then(([supRes, listRes]) => {
        if (cancelled) return;
        setSupportedKeys(supRes?.data?.data?.supported_provider_keys || []);
        const used = (listRes?.data?.data?.providers || []).map((p) => p.provider_key);
        setUsedKeys(used);
      })
      .catch(() => {
        if (cancelled) return;
        message.error("Failed to load supported provider keys");
      })
      .finally(() => {
        if (cancelled) return;
        setOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible]);

  useEffect(() => {
    if (visible) {
      setTestModelId("");
      setTestPrompt(DEFAULT_TEST_PROMPT);
      setTestResult(null);
      setModelTestResults({});
      setModelTestLoading({});
      setCreationBlocked(false);
      setFailedModels([]);
      pendingPayloadRef.current = null;
    }
  }, [visible]);

  const handleProviderKeyChange = (key) => {
    const suggested = DEFAULT_TEST_MODEL[key] || "";
    setTestModelId(suggested);
    setTestResult(null);
  };

  const buildPayload = (values) => ({
    provider_key: values.provider_key,
    name: values.name?.trim(),
    api_key: values.api_key?.trim(),
    enabled: values.enabled ?? true,
    models: (values.models || [])
      .filter((m) => m && m.model_id?.trim())
      .map((m) => ({
        model_id: m.model_id.trim(),
        display_name: m.display_name?.trim() || null,
        enabled: m.enabled ?? true,
        input_cost_per_million: m.input_cost_per_million ?? 0,
        output_cost_per_million: m.output_cost_per_million ?? 0,
      })),
  });

  // Run a smoke test for ONE model in the list. Returns the result so the
  // caller can drive UI based on success/failure synchronously.
  const runModelTest = async (modelId) => {
    const providerKey = form.getFieldValue("provider_key");
    const apiKey = form.getFieldValue("api_key")?.trim();

    if (!providerKey || !apiKey) {
      message.error("Provider key and API key are required before testing");
      return null;
    }
    if (!modelId) {
      message.error("Enter a model ID first");
      return null;
    }

    setModelTestLoading((prev) => ({ ...prev, [modelId]: true }));
    try {
      const response = await test_ai_provider_connection({
        provider_key: providerKey,
        api_key: apiKey,
        model_id: modelId,
        prompt: DEFAULT_TEST_PROMPT,
      });
      const data = response.data;
      let result;
      if (data.status === "success" && data.data) {
        result = data.data;
      } else {
        result = {
          success: false,
          provider_key: providerKey,
          model_id: modelId,
          response_text: null,
          error_message: data.message || "Unexpected response from server",
          latency_ms: null,
          tokens_used: null,
          estimated_cost: null,
        };
      }
      setModelTestResults((prev) => ({ ...prev, [modelId]: result }));
      return result;
    } catch (error) {
      console.error(error);
      const result = {
        success: false,
        provider_key: providerKey,
        model_id: modelId,
        response_text: null,
        error_message: extractApiError(error, "Request failed"),
        latency_ms: null,
        tokens_used: null,
        estimated_cost: null,
      };
      setModelTestResults((prev) => ({ ...prev, [modelId]: result }));
      return result;
    } finally {
      setModelTestLoading((prev) => ({ ...prev, [modelId]: false }));
    }
  };

  const handleFinish = async (values) => {
    const payload = buildPayload(values);
    pendingPayloadRef.current = payload;
    setCreationBlocked(false);
    setFailedModels([]);

    const models = payload.models;
    if (models.length === 0) {
      onSubmit(payload);
      return;
    }

    // Test every model. Re-use cached passing results so we don't burn the
    // API on models the admin already verified manually.
    setPreflightRunning(true);
    try {
      const results = await Promise.all(
        models.map(async (m) => {
          const cached = modelTestResults[m.model_id];
          if (cached?.success) return { model_id: m.model_id, result: cached };
          const result = await runModelTest(m.model_id);
          return { model_id: m.model_id, result };
        })
      );

      const failures = results.filter((r) => !r.result || r.result.success === false);
      if (failures.length === 0) {
        onSubmit(payload);
      } else {
        setFailedModels(failures);
        setCreationBlocked(true);
      }
    } finally {
      setPreflightRunning(false);
    }
  };

  const handleCreateAnyway = () => {
    if (!pendingPayloadRef.current) return;
    onSubmit(pendingPayloadRef.current);
  };

  const handleRunConnectionTest = async () => {
    const providerKey = form.getFieldValue("provider_key");
    const apiKey = form.getFieldValue("api_key")?.trim();
    const modelId = testModelId?.trim();

    if (!providerKey) {
      message.error("Select a provider key first");
      return;
    }
    if (!apiKey) {
      message.error("Enter the API key before testing");
      return;
    }
    if (!modelId) {
      message.error("Enter a model ID to test against");
      return;
    }

    try {
      setTestLoading(true);
      setTestResult(null);
      const response = await test_ai_provider_connection({
        provider_key: providerKey,
        api_key: apiKey,
        model_id: modelId,
        prompt: testPrompt?.trim() || DEFAULT_TEST_PROMPT,
      });
      const data = response.data;
      if (data.status === "success" && data.data) {
        setTestResult(data.data);
      } else {
        setTestResult({
          success: false,
          provider_key: providerKey,
          model_id: modelId,
          response_text: null,
          error_message: data.message || "Unexpected response from server",
          latency_ms: null,
          tokens_used: null,
          estimated_cost: null,
        });
      }
    } catch (error) {
      console.error(error);
      setTestResult({
        success: false,
        provider_key: providerKey,
        model_id: modelId,
        response_text: null,
        error_message: extractApiError(error, "Request failed"),
        latency_ms: null,
        tokens_used: null,
        estimated_cost: null,
      });
    } finally {
      setTestLoading(false);
    }
  };

  const handleAfterClose = () => {
    form.resetFields();
    setTestModelId("");
    setTestPrompt(DEFAULT_TEST_PROMPT);
    setTestResult(null);
    setModelTestResults({});
    setModelTestLoading({});
    setCreationBlocked(false);
    setFailedModels([]);
    pendingPayloadRef.current = null;
  };

  const availableKeys = supportedKeys.filter((k) => !usedKeys.includes(k));

  // Small status indicator for a model row given its current model_id.
  const renderModelTestStatus = (modelId) => {
    if (!modelId) {
      return (
        <Tag icon={<QuestionCircleOutlined />} color="default">
          untested
        </Tag>
      );
    }
    if (modelTestLoading[modelId]) {
      return <Tag color="processing">testing…</Tag>;
    }
    const r = modelTestResults[modelId];
    if (!r) {
      return (
        <Tag icon={<QuestionCircleOutlined />} color="default">
          untested
        </Tag>
      );
    }
    if (r.success) {
      return (
        <Tag icon={<CheckCircleFilled />} color="success">
          passed
          {r.latency_ms != null
            ? ` (${(r.latency_ms / 1000).toFixed(2)}s)`
            : ""}
        </Tag>
      );
    }
    return (
      <Tooltip title={r.error_message || "Test failed"}>
        <Tag icon={<CloseCircleFilled />} color="error" style={{ cursor: "help" }}>
          failed
        </Tag>
      </Tooltip>
    );
  };

  const submitInFlight = preflightRunning || loading;

  return (
    <Modal
      title="Add AI Provider"
      open={visible}
      onCancel={onClose}
      afterClose={handleAfterClose}
      footer={null}
      destroyOnClose
      width={720}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={handleFinish}
        initialValues={{ enabled: true, models: [] }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <Space size={4}>
                  Provider Key
                  <Tooltip title="The backend implementation slug. Must come from the supported list and is immutable after creation.">
                    <InfoCircleOutlined style={{ color: "#888" }} />
                  </Tooltip>
                </Space>
              }
              name="provider_key"
              rules={[{ required: true, message: "Provider key is required" }]}
            >
              <Select
                placeholder="Select provider"
                loading={optionsLoading}
                showSearch
                optionFilterProp="children"
                onChange={handleProviderKeyChange}
              >
                {availableKeys.map((k) => (
                  <Option key={k} value={k}>{k}</Option>
                ))}
                {availableKeys.length === 0 && !optionsLoading && (
                  <Option disabled value="">All supported providers already configured</Option>
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Display Name"
              name="name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input placeholder="e.g. OpenAI Primary" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              label="API Key"
              name="api_key"
              rules={[{ required: true, message: "API key is required" }]}
              extra="Stored as-is and never returned in plaintext after save."
            >
              <Input.Password placeholder="sk-..." autoComplete="new-password" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Enabled" name="enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        {/* ── Standalone smoke test panel ───────────────────────────────── */}
        <Divider style={{ margin: "8px 0 12px" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Test connection (optional)
          </Text>
        </Divider>

        <div
          style={{
            padding: 12,
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 6,
            marginBottom: 16,
          }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Text
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  color: "#888",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Test Model
              </Text>
              <Input
                value={testModelId}
                onChange={(e) => setTestModelId(e.target.value)}
                placeholder="Pick a provider first"
                size="middle"
              />
            </Col>
            <Col span={12}>
              <Text
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  color: "#888",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Test Prompt
              </Text>
              <Input
                value={testPrompt}
                onChange={(e) => setTestPrompt(e.target.value)}
                placeholder={DEFAULT_TEST_PROMPT}
                size="middle"
              />
            </Col>
          </Row>

          <Button
            icon={<ExperimentOutlined />}
            onClick={handleRunConnectionTest}
            loading={testLoading}
            style={{ marginTop: 12 }}
          >
            Test Connection
          </Button>

          {testResult && (
            <div style={{ marginTop: 12 }}>
              <SmokeTestResult result={testResult} />
            </div>
          )}
        </div>

        <Divider style={{ margin: "0 0 12px" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Initial models (optional — each is smoke-tested before save)
          </Text>
        </Divider>

        <Form.List name="models">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div
                  key={key}
                  style={{
                    padding: 12,
                    marginBottom: 12,
                    background: "#fafafa",
                    border: "1px solid #f0f0f0",
                    borderRadius: 6,
                  }}
                >
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        label="Model ID"
                        name={[name, "model_id"]}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 8 }}
                      >
                        <Input placeholder="gpt-4o" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        label="Display Name"
                        name={[name, "display_name"]}
                        style={{ marginBottom: 8 }}
                      >
                        <Input placeholder="GPT-4o" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={12} align="bottom">
                    <Col span={8}>
                      <Form.Item
                        {...restField}
                        label="Input $ / 1M"
                        name={[name, "input_cost_per_million"]}
                        initialValue={0}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        {...restField}
                        label="Output $ / 1M"
                        name={[name, "output_cost_per_million"]}
                        initialValue={0}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        label="Enabled"
                        name={[name, "enabled"]}
                        valuePropName="checked"
                        initialValue={true}
                        style={{ marginBottom: 0 }}
                      >
                        <Switch size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={4} style={{ textAlign: "right" }}>
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      >
                        Remove
                      </Button>
                    </Col>
                  </Row>

                  {/* ── Per-row test button + status ──────────────────── */}
                  <Form.Item
                    shouldUpdate={(prev, cur) =>
                      prev?.models?.[name]?.model_id !== cur?.models?.[name]?.model_id
                    }
                    noStyle
                  >
                    {() => {
                      const modelId = form.getFieldValue(["models", name, "model_id"])?.trim();
                      return (
                        <div
                          style={{
                            marginTop: 10,
                            paddingTop: 10,
                            borderTop: "1px dashed #e8e8e8",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <Space size={8}>
                            <Button
                              size="small"
                              icon={<ExperimentOutlined />}
                              onClick={() => runModelTest(modelId)}
                              loading={!!modelTestLoading[modelId]}
                              disabled={!modelId}
                            >
                              Test model
                            </Button>
                            {renderModelTestStatus(modelId)}
                          </Space>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Saving the provider will test untested models automatically.
                          </Text>
                        </div>
                      );
                    }}
                  </Form.Item>
                </div>
              ))}
              <Button
                type="dashed"
                onClick={() => add({ enabled: true, input_cost_per_million: 0, output_cost_per_million: 0 })}
                block
                icon={<PlusOutlined />}
              >
                Add Model
              </Button>
            </>
          )}
        </Form.List>

        {/* ── Preflight failure alert ────────────────────────────────────── */}
        {creationBlocked && failedModels.length > 0 && (
          <Alert
            type="error"
            showIcon
            icon={<WarningFilled />}
            style={{ marginTop: 16 }}
            message={
              <span>
                {failedModels.length} model{failedModels.length > 1 ? "s" : ""} failed
                {" "}the smoke test
              </span>
            }
            description={
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                {failedModels.map((f) => (
                  <div key={f.model_id}>
                    <Text code style={{ fontSize: 12 }}>{f.model_id}</Text>
                    {f.result?.error_message && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="danger" style={{ fontSize: 12 }}>
                          {f.result.error_message}
                        </Text>
                      </div>
                    )}
                  </div>
                ))}
                <div>
                  <Button
                    danger
                    type="primary"
                    size="small"
                    onClick={handleCreateAnyway}
                    loading={loading}
                  >
                    Create provider anyway
                  </Button>
                </div>
              </Space>
            }
          />
        )}

        <Form.Item style={{ marginTop: 20, marginBottom: 0 }}>
          <Row gutter={8} justify="end">
            <Col>
              <Button onClick={onClose} disabled={submitInFlight}>Cancel</Button>
            </Col>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitInFlight}
              >
                {preflightRunning ? "Testing models…" : "Create Provider"}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateProviderModal;
